import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { AgGridReact } from "ag-grid-react";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  CellClassParams,
  CellEditingStoppedEvent,
  CellEditRequestEvent,
  CellStyle,
  ColDef,
  EditableCallbackParams,
  GetRowIdParams,
  GridReadyEvent,
  IRowNode,
  RowSelectedEvent,
  SelectionChangedEvent,
} from "ag-grid-community";
import { Box, Divider, Flex, Text } from "@chakra-ui/react";
import { times, uniq } from "lodash";
import { produce } from "immer";
import {
  CellEditingStartedEvent,
  CellKeyDownEvent,
  CellMouseOutEvent,
  CellMouseOverEvent,
} from "ag-grid-community/dist/lib/events";
import useOrderSheetProductLikes from "@/features/order-sheet-sets/hooks/use-order-sheet-product-likes";
import ProductImageCell, {
  PRODUCT_IMAGE_SIZE,
} from "@/features/order-sheet-sets/product-image-cell";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import {
  FlatProduct,
  FlatSheetKey,
  flatTabsFamily,
  inflatedTabsFamily,
  lineSheetSetFamily,
  orderSheetHasDiscountConditionFamily,
} from "@/features/line-sheet-sets/helpers/sheet-state";
import useInflatedTabs from "@/features/line-sheet-sets/helpers/use-inflated-tabs";
import ProductCheckboxCell from "@/features/order-sheet-sets/product-checkbox-cell";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import SheetHeader from "@/features/order-sheet-sets/sheet-header";
import CollapsibleSheetHeader from "@/features/order-sheet-sets/collapsible-sheet-header";
import SheetCheckBoxHeader from "@/features/order-sheet-sets/sheet-checkbox-header";
import ProductImageHeader from "@/features/order-sheet-sets/product-image-header";
import { MAX_PRODUCT_IMAGE_COUNT } from "@/features/order-sheet-sets/representative-product-image";
import OverlappedValueCell from "@/features/ui/overlapped-value-cell";
import OverlappedNumberEditor from "@/features/ui/overlapped-number-editor";
import OverlappedConditionTypeCell from "@/features/ui/overlapped-condition-type-cell";
import OrderSheetIvaCell from "@/features/order-sheet-sets/order-sheet-iva-cell";
import TakeAllProductsCell from "@/features/ui/take-all-products-cell";
import HighlightCellRenderer from "@/shared/components/ag-grid/highlight-cell-renderer";
import { FloatingBillBoardBar } from "@/features/order-sheet-sets/floating-bill-board-bar";
import {
  DenseOrderSheetSet,
  OrderSheetSet,
} from "@/features/order-sheets/order-sheet.type";
import { orderSheetPageSearchKeywordStore } from "@/features/order-sheet-sets/recoils/order-sheet-page-store";
import useOrderSheetCurrency from "@/features/order-sheet-sets/hooks/use-order-sheet-currency";
import useOrderSheetSetPagePersistedQuery from "@/features/order-sheet-sets/hooks/use-order-sheet-set-page-persisted-query";
import NoResults from "@/shared/components/no-results";
import {
  EMPTY_SHEET_TAB_FILTER_QUERY,
  isSheetTabFilterActive,
} from "@/features/order-sheet-sets/sheet-tab-filter";
import HtmlCellRenderer from "@/shared/components/ag-grid/html-cell-renderer";
import CenteredSpinner from "@/shared/components/centered-spinner";
import { OrderSheetTabBottomBar } from "@/features/order-sheet-sets/order-sheet-tab-bottom-bar";
import { UpdateLineSheetSetOrderRulesResponse } from "@/features/line-sheet-sets/hooks/use-update-line-sheet-set-order-rules";
import SheetInfoPanel from "@/features/line-sheet-sets/sheet-info-panel";
import {
  LineSheetProductMarketPrice,
  LineSheetSetMarketPricing,
} from "@/features/order-sheet-sets/hooks/use-line-sheet-set-market-prices";
import {
  EstimatedSupplyBreakdown,
  getEstimatedSupplyBreakdown,
} from "@/features/order-sheet-sets/utils/order-sheet-market-product";
import LineSheetProductMarketPriceCell, {
  LineSheetProductMarketPriceControl,
} from "@/features/order-sheet-sets/order-sheet-market-price-cell";
import OrderSheetMarketSizeModal from "@/features/order-sheet-sets/order-sheet-market-size-modal";
import { useMarketExternalLink } from "@/features/order-sheet-sets/order-sheet-market-external-link";

interface BuyerOrderSheetProps {
  sheetKey: FlatSheetKey;
  lineSheetSetId: number;
  tabIndex: number;
  isLoading: boolean;
  onEditing?: (isEditing: boolean) => void;
  onSelectedProductIdsChange: (ids: number[]) => void;
  onGridReady: (event: GridReadyEvent<FlatProduct>) => void;
  /** 별을 눌러 둔 상품만 볼지 — 카드 보기와 같은 토글을 공유한다. */
  likedOnly?: boolean;
  isOrderEditable?: boolean;
  isConfirmedEditable?: boolean;
  isConditionEditable?: boolean;
  onUpdatingInflatedProductComplete: (isCompleted: boolean) => void;
  orderSheetSet?: DenseOrderSheetSet;
  selectOrderSheetId?: number;
  revisionNumber?: number;
  isOrderRulesPanelOpen?: boolean;
  handleOrderRulesToggle?: () => void;
  getDenseOrderSheetSetData?: any;
  handleOrderRulesSave?: (
    response: UpdateLineSheetSetOrderRulesResponse
  ) => void;
  /** 상품 id → 크림·포이즌 시세. 패스픽 멤버가 아니면 undefined 이고 시세 열이 숨는다. */
  marketPriceByProductId?: Map<number, LineSheetProductMarketPrice>;
  /** 예상 매입가 계산 재료(환율·정액 수수료) — 공급가는 그리드의 현재 값을 쓴다. */
  marketPricing?: LineSheetSetMarketPricing;
}

function getRowId(params: GetRowIdParams<FlatProduct>) {
  return params.data.rowId;
}

// 헤더의 −/+ 로 접었다 펼 수 있는 칼럼 (브랜드/SKU ~ Markup/%/전량 구매)
const COLLAPSIBLE_COL_IDS = [
  "brandSKUEtc",
  "genderCategorySubcategory",
  "colorCodeColorNameMaterial",
  "searchLinkIcons",
  "marketPrice",
  "priceAmount",
  "priceAdjustedByPartial",
] as const;
type CollapsibleColId = (typeof COLLAPSIBLE_COL_IDS)[number];

function isCollapsibleColId(colId: string): colId is CollapsibleColId {
  return (COLLAPSIBLE_COL_IDS as readonly string[]).includes(colId);
}

// 헤더 셀 좌우 패딩(4px씩) 안에 + 버튼(16px)이 들어가는 폭
const COLLAPSED_COLUMN_WIDTH = 28;

function EmptyCellRenderer() {
  return null;
}

function isEditableCell(isEditable: boolean, params: any) {
  if (isEditable) {
    const rowIndex = params.node.rowIndex;
    const i = parseInt(params.column.getId().split("_")[1]);
    if (rowIndex !== null && rowIndex % 3 === 2) {
      const stockRow = params.api.getDisplayedRowAtIndex(rowIndex - 1);
      if (stockRow && stockRow.data && stockRow.data.sizes) {
        const stockQuantity = stockRow.data.sizes[i] as number;
        return stockQuantity === -1 || stockQuantity > 0;
      }
    }
  }
  return false;
}

export function BuyerOrderSheet({
  sheetKey,
  lineSheetSetId,
  tabIndex,
  isLoading,
  isOrderEditable = false,
  onEditing,
  onSelectedProductIdsChange,
  onGridReady,
  likedOnly = false,
  isConfirmedEditable = false,
  isConditionEditable = false,
  onUpdatingInflatedProductComplete,
  orderSheetSet,
  selectOrderSheetId,
  revisionNumber,
  isOrderRulesPanelOpen,
  handleOrderRulesToggle,
  handleOrderRulesSave,
  getDenseOrderSheetSetData,
  marketPriceByProductId,
  marketPricing,
}: BuyerOrderSheetProps) {
  const { warning: showWarning } = useAppToasts();

  const flatSheetState = useRecoilValue(flatTabsFamily(sheetKey));

  const lineSheetSetState = useRecoilValue(
    lineSheetSetFamily({ lineSheetSetId: lineSheetSetId })
  );

  const { isLiked: isProductLiked, toggle: toggleProductLike } =
    useOrderSheetProductLikes(sheetKey, tabIndex);

  const rowData = useMemo(() => {
    if (flatSheetState && tabIndex > -1) {
      return flatSheetState.tabs
        .find((tab) => tab.index === tabIndex)
        ?.products.filter(
          (product) =>
            product.isFiltered && (!likedOnly || isProductLiked(product.id))
        );
    }
    return undefined;
  }, [flatSheetState, tabIndex, likedOnly, isProductLiked]);

  const rowDataNotFiltered = useMemo(() => {
    // 관심 상품만 보는 중에는 '필터에 걸린 나머지'까지 붙이면 무엇을 보고 있는지 흐려진다.
    if (likedOnly) {
      return [];
    }
    if (flatSheetState && tabIndex > -1 && orderSheetSet?.lineSheetSet.issuedByBoutique.id != 141) {
      return flatSheetState.tabs
        .find((tab) => tab.index === tabIndex)
        ?.products.filter((product) => !product.isFiltered).map((p) => {
          return {
            ...p,
            rowId: `unfiltered-${p.rowId}`
          }
        }) || [];
    }
    return [];
  }, [
    flatSheetState,
    orderSheetSet?.lineSheetSet.issuedByBoutique.id,
    tabIndex,
    likedOnly,
  ]);

  const { value: query, set: setQuery } = useOrderSheetSetPagePersistedQuery();

  const { updateInflatedProduct, getInflatedProductById } = useInflatedTabs();
  const gridRef = useRef<AgGridReact<FlatProduct> | null>(null);
  const [isGridRefSet, setIsGridRefSet] = useState(false);

  // colDefs 는 useState 로 한 번만 만들어져서 cellRendererParams 가 첫 렌더의 클로저를
  // 붙잡는다. 함수를 그대로 넘기면 별을 눌러도 늘 처음 상태만 보여 켜지지 않는다.
  // ref 를 한 겹 두고 항상 최신 것을 읽게 한다.
  const likeRef = useRef({
    isLiked: isProductLiked,
    toggle: toggleProductLike,
  });
  likeRef.current = { isLiked: isProductLiked, toggle: toggleProductLike };

  const likeControl = useMemo(
    () => ({
      isLiked: (productId: number) => likeRef.current.isLiked(productId),
      // 셀 파라미터만 바뀌어서는 ag-grid 가 다시 그리지 않는다 — 이미지 열만 강제로 새로 그린다.
      onToggle: (productId: number) =>
        likeRef.current.toggle(productId, () => {
          gridRef.current?.api?.refreshCells({ columns: ["i"], force: true });
        }),
    }),
    []
  );

  // 시세 열도 같은 이유로 ref 를 거친다 — 시세는 화면이 뜬 뒤에 도착한다.
  const marketPriceRef = useRef(marketPriceByProductId);
  marketPriceRef.current = marketPriceByProductId;
  // 예상 매입가는 상품의 '지금' 공급가로 계산한다 — 컨디션을 고치면 공급가가 바뀌고
  // 예상 매입가·마진도 따라 바뀌어야 한다. 셀이 그때그때 최신 상태를 읽도록 ref 에 담는다.
  const estimateRef = useRef<
    (productId: number) => EstimatedSupplyBreakdown | null
  >(() => null);
  estimateRef.current = (productId: number) =>
    getEstimatedSupplyBreakdown(
      getInflatedProductById(sheetKey, tabIndex, productId)?.supplyPrice,
      marketPriceByProductId?.get(productId),
      marketPricing
    );
  // 사이즈별 시세 모달 — 열려 있는 상품 id. 상품은 열 때 인플레이트 상태에서 찾는다.
  const [marketSizeProductId, setMarketSizeProductId] = useState<number | null>(
    null
  );
  // 시세 가격을 눌러 마켓으로 나가기 — 포이즌 안내 모달은 그리드에 하나만 둔다(open 은 참조가 고정이다).
  const { open: openMarketExternal, modal: marketExternalModal } =
    useMarketExternalLink();
  const marketPriceControl = useMemo<LineSheetProductMarketPriceControl>(
    () => ({
      get: (productId: number) => marketPriceRef.current?.get(productId),
      getEstimate: (productId: number) => estimateRef.current(productId),
      openSizeDetail: (productId: number) => setMarketSizeProductId(productId),
      openExternal: openMarketExternal,
    }),
    [openMarketExternal]
  );
  const isMarketPriceEnabled = marketPriceByProductId !== undefined;

  const { t, tCurrencyString } = useI18nHelper();

  const setOrderSheetSearchKeyword = useSetRecoilState(
    orderSheetPageSearchKeywordStore
  );

  const [isImagesCollapsed, setIsImagesCollapsed] = useState<boolean>(true);

  const [collapsedColIds, setCollapsedColIds] = useState<
    ReadonlySet<CollapsibleColId>
  >(() => new Set());

  const toggleColumnCollapse = useCallback((colId: string) => {
    if (!isCollapsibleColId(colId)) return;
    setCollapsedColIds((prev) => {
      const next = new Set(prev);
      if (next.has(colId)) {
        next.delete(colId);
      } else {
        next.add(colId);
      }
      return next;
    });
  }, []);

  const components = useMemo<{ [p: string]: any }>(() => {
    return {
      agColumnHeader: SheetHeader,
    };
  }, []);

  // DISCOUNT 컨디션 상품이 하나도 없으면 IVA 컬럼은 빈 칸만 남아서 통째로 감춘다.
  const hasDiscountCondition = useRecoilValue(
    orderSheetHasDiscountConditionFamily({ key: sheetKey, tabIndex })
  );
  // 시트 통화(lineSheetSet.currency)가 비어 있을 때 "EUR"로 폴백하면
  // 원화(KRW) 시트의 가격·합계가 €로 잘못 표기된다(값은 KRW인데 심볼만 €).
  // 실제 상품 가격의 통화(priceCurrency)를 폴백으로 사용해 통화를 일치시킨다.
  const productCurrency = useMemo(
    () => rowData?.find((p) => p.priceCurrency)?.priceCurrency || undefined,
    [rowData]
  );
  const { isKrw, exchangeCurrencyInfo, selectedCurrency, exchangeCurrencyValue } =
    useOrderSheetCurrency(
      lineSheetSetState?.lineSheetSet.currency || productCurrency
    );

  // 바이어 컨디션 적용 시에도 공급가에 수수료(commission)를 반영하기 위한 설정.
  // (IVA 계산식은 calcPrice가 컨디션의 applyIva/ivaValue로 적용)
  const priceConfig = useMemo(
    () => ({
      commission: lineSheetSetState?.lineSheetSet?.commission,
      commissionType: lineSheetSetState?.lineSheetSet?.commissionType,
    }),
    [
      lineSheetSetState?.lineSheetSet?.commission,
      lineSheetSetState?.lineSheetSet?.commissionType,
    ]
  );

  const [colDefs, setColDefs] = useState<ColDef<FlatProduct>[]>([
    {
      cellRenderer: ProductCheckboxCell,
      rowSpan: (params) => {
        if (params.node && params.node.rowIndex !== null) {
          return params.node.rowIndex % 3 === 0 ? 3 : 1;
        }
        return 1;
      },
      width: 40,
      pinned: "left",
      field: "isChecked",
      headerName: "",
      resizable: false,
      suppressMovable: true,
      headerComponent: SheetCheckBoxHeader,
      cellClassRules: {
        "product-odd-row": (params) => params.rowIndex % 6 === 0,
        "product-even-row": (params) => params.rowIndex % 6 === 3,
        "product-checkbox-cell": (params) => params.rowIndex % 3 === 0,
      },
      headerClass: "product-checkbox-header",
    },
    {
      valueGetter: (params) => {
        if (params.data) {
          return {
            imageUrls: params.data.imageUrls?.map((imageUrl) => {
              return {
                url: imageUrl.url,
                type: imageUrl.type,
              };
            }),
            id: params.data?.id,
            searchWord: params.data?.searchWord,
          };
        }
        return null;
      },
      cellStyle: { fontSize: "12px" },
      cellRenderer: ProductImageCell,
      width: PRODUCT_IMAGE_SIZE,
      autoHeight: false,
      rowSpan: (params) => {
        if (params.node && params.node.rowIndex !== null) {
          return params.node.rowIndex % 3 === 0 ? 3 : 1;
        }
        return 1;
      },
      resizable: false,
      pinned: "left",
      field: "i",
      headerName: "",
      headerClass: "product-image-header",
      headerComponent: ProductImageHeader,
      headerComponentParams: {
        isCollapsed: isImagesCollapsed,
        onClick: (isCollapse: boolean) => {
          setIsImagesCollapsed(isCollapse);
        },
      },
      cellRendererParams: {
        isCollapsed: isImagesCollapsed,
        isLineSheet: false,
        likeControl,
      },
      cellClassRules: {
        "product-odd-row": (params) => params.rowIndex % 6 === 0,
        "product-even-row": (params) => params.rowIndex % 6 === 3,
      },
    },
    {
      field: "brandSKUEtc",
      autoHeaderHeight: true,
      width: 160,
      resizable: true,
      pinned: "left",
      cellStyle: { fontSize: "12px" },
      headerComponentParams: {
        items: ["brand", "sku", "etc_info"],
      },
      cellRenderer: HighlightCellRenderer,
      cellClassRules: {
        "product-odd-row": (params) => params.rowIndex % 6 < 3,
        "product-even-row": (params) => params.rowIndex % 6 >= 3,
      },
    },
    {
      field: "genderCategorySubcategory",
      width: 120,
      resizable: true,
      pinned: "left",
      cellStyle: { fontSize: "12px" },
      headerComponentParams: {
        items: ["gender", "category", "subcategory"],
      },
      cellRenderer: HighlightCellRenderer,
      cellClassRules: {
        "product-odd-row": (params) => params.rowIndex % 6 < 3,
        "product-even-row": (params) => params.rowIndex % 6 >= 3,
      },
    },
    {
      field: "colorCodeColorNameMaterial",
      width: 120,
      resizable: true,
      pinned: "left",
      cellStyle: { fontSize: "12px" },
      headerComponentParams: {
        items: ["season", "color", "material"],
      },
      cellRenderer: HighlightCellRenderer,
      cellClassRules: {
        "product-odd-row": (params) => params.rowIndex % 6 < 3,
        "product-even-row": (params) => params.rowIndex % 6 >= 3,
      },
    },
    {
      field: "searchLinkIcons",
      width: 120,
      resizable: true,
      pinned: "left",
      cellStyle: { fontSize: "12px" },
      headerComponentParams: {
        items: ["", "search_link_icons", ""],
      },
      hide: lineSheetSetState?.lineSheetSet.type === "SEASON",
      cellClassRules: {
        "product-odd-row": (params) => params.rowIndex % 6 < 3,
        "product-even-row": (params) => params.rowIndex % 6 >= 3,
      },
      cellRenderer: HtmlCellRenderer,
      headerClass: "header-center", //우측 정렬
    },
    {
      // 크림·포이즌 시세(패스픽 멤버만). 줄마다 예상 매입가(원화, +사이즈별 보기) / KREAM / POIZON.
      colId: "marketPrice",
      width: 190,
      resizable: true,
      pinned: "left",
      hide: true,
      cellStyle: { padding: "0 8px" },
      headerComponentParams: {
        items: ["estimated_landed_cost", "kream", "poizon"],
      },
      headerClass: "header-right",
      // 값이 없는 열이라 그대로 두면 ag-grid 가 행 데이터가 바뀌어도 이 셀을 다시 그리지 않는다.
      // 예상 매입가를 값으로 돌려줘서, 컨디션 수정으로 공급가가 바뀌면 세 줄이 같이 다시 그려지게 한다.
      valueGetter: (params) =>
        params.data && params.data.id >= 0
          ? marketPriceControl.getEstimate(params.data.id)?.value ?? null
          : null,
      cellRenderer: LineSheetProductMarketPriceCell,
      cellRendererParams: {
        control: marketPriceControl,
      },
      cellClassRules: {
        "product-odd-row": (params) => params.rowIndex % 6 < 3,
        "product-even-row": (params) => params.rowIndex % 6 >= 3,
      },
    },
    {
      field: "priceAmount",
      cellStyle: (params): CellStyle => {
        const cellStyle: CellStyle = {
          textAlign: "end",
          fontSize: "11px",
          fontWeight: "400",
          color: "#54565F",
          padding: "0",
        };
        const rowIndex = params.node?.rowIndex;
        if (typeof rowIndex === "number" && rowIndex % 3 === 0) {
          cellStyle.color = "#303136";
          cellStyle.fontSize = "12px";
          cellStyle.fontWeight = "700";
        }
        return cellStyle;
      },
      width: 120,
      resizable: false,
      valueGetter: (params) => {
        const value = params.data?.priceAmount;
        // 도매가/소매가 등 행마다 통화가 다를 수 있으므로 시트 통화가 아닌
        // 각 행의 priceCurrency를 기준으로 표시/환산한다. (없으면 시트 통화로 폴백)
        const currency =
          params.data?.priceCurrency ||
          lineSheetSetState?.lineSheetSet?.currency;
        const exchangeCurrency = currency
          ? exchangeCurrencyInfo[currency]?.pathrade_send_rate
          : 0;
        if (value !== undefined) {
          // 가격이 없는 행(예: 도매가 없음)은 0 대신 "-"
          if (typeof value !== "object" && params.data?.isPriceMissing) {
            return "-";
          }
          if (typeof value !== "object") {
            return tCurrencyString(
              isKrw ? Math.round(value * exchangeCurrency) : value,
              {
                type: "SYMBOL",
                code: isKrw ? "KRW" : currency,
              }
            );
          } else {
            return {
              top: tCurrencyString(
                isKrw ? Math.round(value.top * exchangeCurrency) : value.top,
                {
                  type: "SYMBOL",
                  code: isKrw ? "KRW" : currency,
                }
              ),
              under: tCurrencyString(
                isKrw
                  ? Math.round(value.under * exchangeCurrency)
                  : value.under,
                {
                  type: "SYMBOL",
                  code: isKrw ? "KRW" : currency,
                }
              ),
            };
          }
        }
      },
      cellRendererSelector: (params) => {
        const value = params.data?.priceAmount;
        if (value !== undefined) {
          if (typeof value == "object") {
            {
              return {
                component: OverlappedValueCell,
                params: {
                  topTextAlign: "end",
                },
              };
            }
          }
        }
        return undefined;
      },
      pinned: "left",
      headerComponentParams: {
        items: ["supply_price", "cost", "retail"],
      },
      headerClass: "header-right", //우측 정렬
      cellClassRules: {
        "product-odd-row": (params) => params.rowIndex % 6 < 3,
        "product-even-row": (params) => params.rowIndex % 6 >= 3,
        "product-price-cell": (params) => params.rowIndex % 3 === 0,
        "product-not-selectable-cell": (params) => {
          const rowIndex = params.node.rowIndex;
          if (
            params.rowIndex % 3 === 0 &&
            rowIndex !== null &&
            params.node.parent?.childrenAfterFilter?.[rowIndex]?.data
              ?.priceAmount && // supply price
            !params.node.parent?.childrenAfterFilter?.[rowIndex + 1]?.data
              ?.priceAmount && // cost
            !params.node.parent?.childrenAfterFilter?.[rowIndex + 2]?.data
              ?.priceAmount // retail
          ) {
            return true;
          }
          return false;
        },
      },
    },
    {
      headerName: "IVA",
      colId: "ivaCondition",
      pinned: "left",
      width: 100,
      resizable: false,
      editable: false,
      cellStyle: {
        overflow: "visible",
        padding: "1px",
        paddingLeft: "4px",
      },
      cellRenderer: OrderSheetIvaCell,
      valueGetter: (params) => {
        const moldIndex =
          params.node != null && params.node.rowIndex != null
            ? params.node.rowIndex % 3
            : -1;
        if (moldIndex === 0) return params.data?.applyIva;
        if (moldIndex === 1) return params.data?.ivaValue;
        return null;
      },
      cellClassRules: {
        "product-odd-row": (params) => params.rowIndex % 6 < 3,
        "product-even-row": (params) => params.rowIndex % 6 >= 3,
      },
    },
    {
      field: "priceAdjustedByPartial",
      headerName: "",
      pinned: "left",
      width: 104,
      resizable: false,
      colId: "priceAdjustedByPartial",
      // 헤더 문구는 없지만 세 줄 높이를 채워야 접기 버튼이 다른 칼럼처럼 맨 윗줄에 온다.
      headerComponentParams: {
        items: ["", "", ""],
      },
      // cellRenderer: PriceAdjustedByCell,
      cellStyle: {
        overflow: "visible",
        padding: "1px",
        paddingLeft: "8px",
      },
      valueGetter: (params) => {
        //cell re render가 되려면 이 값이 변경되어함,
        //custom cell 에서 value로 접근 가능
        const moldIndex =
          params.node != null && params.node.rowIndex != null
            ? params.node.rowIndex % 3
            : -1;
        if (moldIndex === 2) {
          return params.data?.sizes;
        }

        return params.data?.priceAdjustedByPartial;
      },
      cellRendererSelector: (params) => {
        const rowIndex = params.node.rowIndex;
        if (rowIndex != null) {
          const index = rowIndex % 3;
          if (index === 0) {
            return {
              component: OverlappedConditionTypeCell,
              params: {
                isConditionFixed: params.data?.isConditionFixed,
                applyIva: params.data?.applyIva,
              },
            };
          } else if (index === 1) {
            return {
              component: OverlappedValueCell,
              params: {
                unit: "%",
              },
            };
          } else if (index === 2) {
            return {
              component: TakeAllProductsCell,
            };
          }
        }

        return undefined;
      },
      cellEditor: OverlappedNumberEditor,
      cellEditorParams: {
        unit: "%",
      },
      editable: (params) => {
        return (
          (!params?.data?.isConditionFixed &&
            params?.node?.rowIndex &&
            params?.node?.rowIndex % 3 === 1) ||
          true
        );
      },
      cellClass: "product-price-adjusted-cell",
      cellClassRules: {
        "product-odd-row": (params) => params.rowIndex % 6 < 3,
        "product-even-row": (params) => params.rowIndex % 6 >= 3,
        "product-price-adjusted-selectbox-cell": (params) =>
          params.rowIndex % 3 === 0,
        "product-price-adjusted-input-cell": (params) =>
          params.rowIndex % 3 === 1,
        "product-editable-cell": (params) => {
          return params.rowIndex % 3 === 1 && !params?.data?.isConditionFixed;
        },
        "product-not-selectable-cell": (params) => {
          if (
            params.rowIndex % 3 === 1 &&
            params.node.parent?.childrenAfterFilter?.[params.rowIndex - 1]?.data
              ?.priceAmount && // supply price
            !params.node.parent?.childrenAfterFilter?.[params.rowIndex]?.data
              ?.priceAmount && // cost
            !params.node.parent?.childrenAfterFilter?.[params.rowIndex + 1]
              ?.data?.priceAmount // retail
          ) {
            return true;
          }
          return false;
        },
      },
    },
  ]);

  const maxProductImageCount = useMemo(() => {
    return (
      rowData
        ?.map((i) =>
          Math.min(MAX_PRODUCT_IMAGE_COUNT, (i.imageUrls || []).length)
        )
        .reduce((acc, cur) => Math.max(acc, cur), 0) || 0
    );
  }, [rowData]);

  useEffect(() => {
    setColDefs((prev) =>
      prev.map((def) =>
        def.field === "priceAmount"
          ? {
              ...def,
              valueGetter: (params) => {
                const value = params.data?.priceAmount;
                // 행마다 통화가 다를 수 있으므로 각 행의 priceCurrency 기준으로 표시/환산.
                const currency =
                  params.data?.priceCurrency ||
                  lineSheetSetState?.lineSheetSet?.currency;
                const exchangeCurrency = currency
                  ? exchangeCurrencyInfo[currency]?.pathrade_send_rate
                  : 0;
                if (value !== undefined) {
                  // 가격이 없는 행(예: 도매가 없음)은 0 대신 "-"
                  if (typeof value !== "object" && params.data?.isPriceMissing) {
                    return "-";
                  }
                  if (typeof value !== "object") {
                    return tCurrencyString(
                      isKrw ? Math.round(value * exchangeCurrency) : value,
                      {
                        type: "SYMBOL",
                        code: isKrw ? "KRW" : currency,
                      }
                    );
                  } else {
                    return {
                      top: tCurrencyString(
                        isKrw
                          ? Math.round(value.top * exchangeCurrency)
                          : value.top,
                        {
                          type: "SYMBOL",
                          code: isKrw ? "KRW" : currency,
                        }
                      ),
                      under: tCurrencyString(
                        isKrw
                          ? Math.round(value.under * exchangeCurrency)
                          : value.under,
                        {
                          type: "SYMBOL",
                          code: isKrw ? "KRW" : currency,
                        }
                      ),
                    };
                  }
                }
              },
            }
          : def
      )
    );
  }, [
    isKrw,
    lineSheetSetState,
    tCurrencyString,
    exchangeCurrencyInfo,
    exchangeCurrencyValue,
    selectedCurrency,
  ]);

  useEffect(() => {
    setColDefs((prev) => {
      return produce(prev, (draft) => {
        const i = draft.findIndex((colDef) => colDef.field === "i");

        let width;
        if (isImagesCollapsed) {
          width = PRODUCT_IMAGE_SIZE;
        } else {
          width =
            PRODUCT_IMAGE_SIZE * maxProductImageCount +
            (maxProductImageCount - 1) * 4 +
            2;
        }
        if (i > -1) {
          draft[i] = {
            ...draft[i],
            width,
            headerComponentParams: {
              ...draft[i].headerComponentParams,
              isCollapsed: isImagesCollapsed,
              isDisabled:
                maxProductImageCount === 0 || maxProductImageCount === 1,
            },
            cellRendererParams: {
              ...draft[i].cellRendererParams,
              isCollapsed: isImagesCollapsed,
            },
          };
        }
      });
    });
  }, [isImagesCollapsed, maxProductImageCount]);

  useEffect(() => {
    //cell params변경 or colDef수정이 필요한경우,

    setColDefs((prev) => {
      return produce(prev, (draft) => {
        const i = draft.findIndex((c) => c.field === "priceAdjustedByPartial");
        if (i > -1) {
          draft[i].cellRendererParams = {
            tabIndex,
            sheetKey,
            isEditable: isConditionEditable,
            isOrderEditable,
          };
          draft[i].editable = (params => {
            const moldIndex =
              params.node.rowIndex != null ? params.node.rowIndex % 3 : -1;
            if (moldIndex === 1) {
              return isConditionEditable && !params.data?.isConditionFixed;
            }
            return false;
          });
          draft[i].cellClassRules!["product-editable-cell"] = (params) => {
            const moldIndex =
              (params.node.rowIndex != null ? params.node.rowIndex : -1) % 3;
            if (moldIndex === 1) {
              return isConditionEditable && !params.data?.isConditionFixed;
            }
            return false;
          };
        }
        const ivaIdx = draft.findIndex((c) => c.colId === "ivaCondition");
        if (ivaIdx > -1) {
          draft[ivaIdx].hide = !hasDiscountCondition;
          draft[ivaIdx].cellRendererParams = {
            tabIndex,
            sheetKey,
            // 컨디션을 고칠 수 있으면 IVA도 셀에서 바로 고칠 수 있다
            // (에이전시 주문서 그리드 order-sheet-tab.tsx와 같은 기준).
            isEditable: isConditionEditable,
            priceConfig,
            onUpdatingInflatedProductComplete,
          };
        }
      });
    });
  }, [
    tabIndex,
    sheetKey,
    isOrderEditable,
    isConfirmedEditable,
    isConditionEditable,
    hasDiscountCondition,
    onUpdatingInflatedProductComplete,
    priceConfig,
  ]);

  const sizeLength = useMemo(() => {
    if (rowData) {
      let maxLength = 0;
      if (rowDataNotFiltered) {
        maxLength = rowDataNotFiltered
          .map((i) => {
            return i.sizes ? i.sizes.length : 0;
          })
          .reduce((acc, cur) => {
            return Math.max(acc, cur);
          }, 0);
      }
      const filteredMaxLength = rowData
        .map((i) => {
          return i.sizes ? i.sizes.length : 0;
        })
        .reduce((acc, cur) => {
          return Math.max(acc, cur);
        }, 0);
      maxLength = Math.max(maxLength, filteredMaxLength);
      return maxLength;
    }
    return 0;
  }, [rowData, rowDataNotFiltered]);

  const isSizeOrQuantityEditable = useCallback(
    (
      params: CellClassParams<FlatProduct> | EditableCallbackParams<FlatProduct>
    ) => {
      const colIndex = parseInt(params.column.getColId().split("_")[1]);
      const index =
        params.node.rowIndex != null ? params.node.rowIndex % 3 : -1;
      if (index === 1) {
        return (
          isConfirmedEditable && params.data?.sizes![colIndex] !== undefined
        );
      } else if (index === 2) {
        if (!isOrderEditable || params.data?.sizes![colIndex] === undefined) {
          return false;
        }
        // 품절(가용재고 0) 옵션은 주문수량 입력도 막는다 — 재고는 바로 윗행 sizes에 있다.
        // (재고 행을 못 찾는 예외 상황에서는 기존처럼 입력을 허용한다)
        const stockRow = params.api.getDisplayedRowAtIndex(
          params.node.rowIndex! - 1
        );
        const stockQuantity = stockRow?.data?.sizes?.[colIndex];
        return stockQuantity === -1 || Number(stockQuantity ?? 1) > 0;
      }
      return false;
    },
    [isConfirmedEditable, isOrderEditable]
  );

  useEffect(() => {
    if (sizeLength > 0) {
      setColDefs((prev) => {
        const i = prev.findLastIndex(
          (p) => p.field === "priceAdjustedByPartial"
        );

        if (i > -1) {
          return [
            ...prev.slice(0, i + 1),
            ...((): ColDef<FlatProduct>[] => {
              return times(sizeLength, (index): ColDef<FlatProduct> => {
                return {
                  headerName: `sq${index}`,
                  headerClass: "product-size-or-quantity-cell-header",
                  colId: `sq_${index}`,
                  width: 80,
                  cellStyle: (params) => {
                    return {
                      textAlign: "center",
                      fontSize: "12px",
                      padding: 0,
                    };
                  },
                  valueGetter: (params) => {
                    const v = params.data?.sizes?.[index];
                    if (v === 0) {
                      // 재고(가운데) 행의 0 = 가용재고 차감 결과 품절. 빈 칸이면 옵션이
                      // 없는 것과 구분이 안 돼 '품절'로 표시한다. 주문수량 행의 0은 기존대로 빈 칸.
                      const rowIndex = params.node?.rowIndex;
                      return rowIndex != null && rowIndex % 3 === 1
                        ? t("sold_out")
                        : "";
                    }
                    return v;
                  },
                  valueFormatter: (params) => {
                    if (params.value === -1) {
                      return "∞";
                    }
                    return params.value === 0 ? "" : params.value;
                  },
                  valueSetter: (params) => {
                    return true;
                  },
                  cellRenderer: OverlappedValueCell,
                  cellEditor: OverlappedNumberEditor,
                  cellClassRules: {
                    "product-odd-row": (params) => params.rowIndex % 6 < 3,
                    "product-even-row": (params) => params.rowIndex % 6 >= 3,
                    "product-each-last-row": (params) =>
                      params.rowIndex % 3 === 2,
                    "product-editable-cell": (params) => {
                      return isSizeOrQuantityEditable(params);
                    },
                  },
                  editable: (params) => {
                    return isSizeOrQuantityEditable(params);
                  },
                  resizable: true,
                  headerComponentParams: {
                    items: ["size", "stock", "quantity.abbr"],
                  },
                };
              });
            })(),
          ];
        }
        return prev;
      });
    }
  }, [
    t,
    tabIndex,
    isOrderEditable,
    sizeLength,
    isConfirmedEditable,
    getInflatedProductById,
    sheetKey,
    updateInflatedProduct,
    isSizeOrQuantityEditable,
  ]);

  useEffect(() => {
    setColDefs((prev) => {
      return produce(prev, (draft) => {
        const i = draft.findIndex(
          (colDef) => colDef.field === "searchLinkIcons"
        );
        if (i > -1) {
          // 시세 열이 보이면 참고 사이트(네이버·크림 검색) 열은 뺀다 — 시세 열의 가격이 매칭된
          // 상품 화면으로 바로 보내 주므로 검색 링크가 겹친다. 시세를 못 보는 사용자에게는 그대로 둔다.
          draft[i].hide =
            lineSheetSetState?.lineSheetSet.type == "SEASON" ||
            isMarketPriceEnabled;
        }
      });
    });
  }, [lineSheetSetState?.lineSheetSet.type, isMarketPriceEnabled]);

  // 시세 열은 멤버십이 확인되면 나타나고, 시세가 도착하면 값만 다시 그린다.
  useEffect(() => {
    setColDefs((prev) =>
      produce(prev, (draft) => {
        const i = draft.findIndex((colDef) => colDef.colId === "marketPrice");
        if (i > -1) {
          draft[i].hide = !isMarketPriceEnabled;
        }
      })
    );
  }, [isMarketPriceEnabled]);

  useEffect(() => {
    gridRef.current?.api?.refreshCells({
      columns: ["marketPrice"],
      force: true,
    });
  }, [marketPriceByProductId, marketPricing]);

  const marketSizeProduct = useMemo(
    () =>
      marketSizeProductId != null
        ? getInflatedProductById(sheetKey, tabIndex, marketSizeProductId) ??
          null
        : null,
    [marketSizeProductId, getInflatedProductById, sheetKey, tabIndex]
  );

  const onCellEditRequest = useCallback(
    async (event: CellEditRequestEvent<FlatProduct>) => {
      const data = event.data;
      const colId = event.column.getColId();
      const rowIndex = event.rowIndex === null ? -1 : event.rowIndex;
      const moldIndex = rowIndex % 3;

      const inflatedProduct = getInflatedProductById(
        sheetKey,
        tabIndex,
        data.id
      );

      if (inflatedProduct) {
        if (colId.startsWith("sq")) {
          // 수량이 변경된 경우

          let newValue = event.newValue;
          if (newValue < 0) {
            newValue = 0;
          }
          if (isNaN(newValue) || newValue === null || newValue === undefined || isNaN(parseFloat(newValue))) {
            newValue = 0;
          }
          const colIndex = parseInt(colId.split("_")[1]);
          const newStock = inflatedProduct.newStockWithOptionList
            ? inflatedProduct.newStockWithOptionList[colIndex].quantity
            : null;
          if (
            moldIndex === 2 &&
            newStock != null &&
            newValue > newStock &&
            newStock !== -1
          ) {
            showWarning(
              t("common:apply_order_quantity.messages.error.out_of_range"),
              "apply_price_condition_form"
            );
            return;
          }
          const nextData = produce(inflatedProduct, (draft) => {
            if (moldIndex === 1 && draft.newStockWithOptionList) {
              draft.newStockWithOptionList[colIndex].quantity = newValue;
            } else if (moldIndex === 2) {
              draft.orderQuantityWithOptionList[colIndex].quantity = newValue;
            }
          });

          updateInflatedProduct(sheetKey, tabIndex, nextData, priceConfig);
          onUpdatingInflatedProductComplete(true);
        } else if (colId === "priceAdjustedByPartial") {
          // condition이 변경된 경우

          if (moldIndex === 0) {
            //priceAdjustedByPartial
            const nextData = produce(inflatedProduct, (draft) => {
              if (draft.confirmedPriceAdjustedBy) {
                draft.confirmedPriceAdjustedBy.by = event.newValue;
              } else {
                draft.priceAdjustedBy.by = event.newValue;
              }
            });
            updateInflatedProduct(sheetKey, tabIndex, nextData, priceConfig);
            onUpdatingInflatedProductComplete(true);
            return;
          } else if (moldIndex === 1) {
            //nan이면 0처리하자
            let newValue = event.newValue;

            if (
              isNaN(newValue) ||
              newValue === null ||
              newValue === undefined
            ) {
              newValue = 0;
            }

            if (newValue > 100 || newValue < -100) {
              showWarning(
                t(
                  "common:apply_price_condition_form.messages.error.out_of_range"
                ),
                "apply_price_condition_form"
              );
              return;
            }
            const nextData = produce(inflatedProduct, (draft) => {
              if (draft.confirmedPriceAdjustedBy) {
                draft.confirmedPriceAdjustedBy.value = newValue;
              } else {
                draft.priceAdjustedBy.value = newValue;
              }
            });
            updateInflatedProduct(sheetKey, tabIndex, nextData, priceConfig);
            onUpdatingInflatedProductComplete(true);
          } else if (moldIndex === 2) {
            const nextData = produce(inflatedProduct, (draft) => {
              const stockData =
                draft.newStockWithOptionList ||
                draft.stockQuantityWithOptionList;

              const orderData = draft.orderQuantityWithOptionList;

              draft.orderQuantityWithOptionList = stockData.map(
                (stockDatum, index) => {
                  const orderQuantity = orderData[index].quantity;
                  return {
                    name: stockDatum.name,
                    quantity:
                      orderQuantity < stockDatum.quantity
                        ? stockDatum.quantity
                        : orderQuantity,
                  };
                }
              );
            });

            updateInflatedProduct(sheetKey, tabIndex, nextData, priceConfig);
            onUpdatingInflatedProductComplete(true);
          }
        }
      }
    },
    [
      t,
      showWarning,
      sheetKey,
      tabIndex,
      getInflatedProductById,
      updateInflatedProduct,
      onUpdatingInflatedProductComplete,
      priceConfig,
    ]
  );

  const handleGridReady = useCallback(
    (event: GridReadyEvent<FlatProduct>) => {
      onGridReady(event);
    },
    [onGridReady]
  );

  const [inflatedTabs, setInflatedTabsFamily] = useRecoilState(
    inflatedTabsFamily(sheetKey)
  );

  /* AG Grid는 cell value의 변경을 감지하려면 onCellEditRequest 이벤트를 사용해야하는데,
   * onCellEditRequest 는 value의 변경이 완료된 이후 발생하는 이벤트임. (커서를 다른 셀로 옮기거나 엔터를 누르거나 등)
   * 따라서 value의 변경을 즉시 감지하려면 onCellKeyDown 이벤트를 사용해야함.
   * onCellValueChanged 콜백이 있으나 동작하지 않음. (https://www.ag-grid.com/javascript-data-grid/grid-events/#reference-editing-cellValueChanged)
   */
  const onCellKeyDown = useCallback(
    (e: CellKeyDownEvent) => {
      if (onEditing) {
        const keyboardEvent = e.event as KeyboardEvent;
        const colDef = e.column.getColDef();
        const isEditable =
          typeof colDef.editable === "function"
            ? colDef.editable(e)
            : colDef.editable === true;
        // 숫자, 백스페이스, . 입력시에만 값이 변경되었다고 보고 isDirty를 true로 변경
        if (
          isEditable &&
          (!isNaN(keyboardEvent.key as any) ||
            ["Backspace", "."].includes(keyboardEvent.key))
        ) {
          setInflatedTabsFamily((prev) => {
            if (prev) {
              const newTabs = prev.tabs.map((tab) => {
                return {
                  ...tab,
                  isDirty: true,
                };
              });
              return {
                key: sheetKey,
                tabs: newTabs,
                isBuyer: prev.isBuyer,
                orderSheetSetStatus: prev.orderSheetSetStatus,
              };
            }
          });
        }
      }
    },
    [onEditing, setInflatedTabsFamily, sheetKey]
  );

  const onCellEditingStarted = useCallback(
    (e: CellEditingStartedEvent) => {
      if (onEditing) {
        onEditing(true);
      }
    },
    [onEditing]
  );

  const onCellEditingStopped = useCallback(
    (e: CellEditingStoppedEvent) => {
      if (onEditing) {
        onEditing(false);
      }
      // 값이 바뀌지 않은 편집 종료에서는 onCellEditRequest가 발사되지 않아
      // 저장 클릭 시 코디네이션 플래그가 안 올라가는 문제가 있어 여기서도 마킹.
      onUpdatingInflatedProductComplete(true);
    },
    [onEditing, onUpdatingInflatedProductComplete]
  );

  const defaultColDef = useMemo((): ColDef<FlatProduct> => {
    return {
      autoHeight: false,
      cellStyle: { fontSize: "12px", padding: "0" },
    };
  }, []);

  // 원본 colDefs 는 여러 effect 가 field/colId 로 찾아 고치므로 건드리지 않고,
  // 그리드에 넘기는 값에서만 접힌 칼럼을 좁은 빈 칼럼으로 바꾼다. 펼치면 원본이 그대로 돌아온다.
  const displayedColDefs = useMemo(
    () =>
      colDefs.map((colDef): ColDef<FlatProduct> => {
        const colId = colDef.colId ?? colDef.field;
        if (!colId || !isCollapsibleColId(colId)) return colDef;

        const isCollapsed = collapsedColIds.has(colId);
        const collapsibleColDef: ColDef<FlatProduct> = {
          ...colDef,
          headerComponent: CollapsibleSheetHeader,
          headerComponentParams: {
            ...colDef.headerComponentParams,
            isCollapsed,
            onToggle: toggleColumnCollapse,
          },
        };
        if (!isCollapsed) return collapsibleColDef;

        // 빈 칸을 눌러 편집이 시작되거나 입력칸 밑줄·선택 강조가 남지 않도록
        // 편집을 막고 행 줄무늬 클래스만 남긴다.
        return {
          ...collapsibleColDef,
          width: COLLAPSED_COLUMN_WIDTH,
          resizable: false,
          cellRenderer: EmptyCellRenderer,
          cellRendererSelector: undefined,
          editable: false,
          cellClass: undefined,
          cellClassRules: {
            "product-odd-row": (params) => params.rowIndex % 6 < 3,
            "product-even-row": (params) => params.rowIndex % 6 >= 3,
          },
        };
      }),
    [colDefs, collapsedColIds, toggleColumnCollapse]
  );

  // 칼럼 정의만 바뀌어서는 ag-grid 가 이미 그린 셀을 다시 그리지 않는다 — 접기 대상 칼럼만 강제로 새로 그린다.
  useEffect(() => {
    gridRef.current?.api?.refreshCells({
      columns: [...COLLAPSIBLE_COL_IDS],
      force: true,
    });
  }, [collapsedColIds]);

  const handleSetGridRef = useCallback(
    (ref: AgGridReact<FlatProduct> | null) => {
      gridRef.current = ref;
      if (ref && !isGridRefSet) {
        setIsGridRefSet(true);
      }
    },
    [isGridRefSet, setIsGridRefSet]
  );

  const isRowSelectable = useCallback((node: IRowNode) => {
    const moldIndex =
      node != null && node.rowIndex != null ? node.rowIndex % 3 : -1;
    const rowIndex = node.rowIndex;

    if (!isConditionEditable) {
      return false
    }

    if (
      moldIndex === 0 &&
      rowIndex !== null &&
      node.parent?.childrenAfterFilter?.[rowIndex]?.data?.priceAmount && // supply price
      !node.parent?.childrenAfterFilter?.[rowIndex + 1]?.data?.priceAmount && // cost
      !node.parent?.childrenAfterFilter?.[rowIndex + 2]?.data?.priceAmount // retail
    ) {
      // 만약 cost/retail이 없고 supply price만 있다면 컨디션 조작 불가능. 최종 공급가가 확정된 경우이기 때문.
      // 원래 isRowSelectable 함수에서 false 리턴 만으로 row의 선택 가능 여부를 판단할 수 있어야할 것 같은데, 제어가 제대로 안됨.
      // node.selectable 에 값을 직접 입력해주어야 함.
      // node의 생성 시점에 설정하고 싶으나 node는 ag-grid가 자동으로 생성하는 것 같음.
      // 여기서 false로 설정해주었더라도 ProductCheckboxCell 에서도 한 번 더 selectable을 체크하지 않으면 최초 로딩 시 체크박스가 선택 가능함.
      node.selectable = false;
      return false;
    }
    return true;
  }, [isConditionEditable]);

  const [checkedProductIds, setCheckedProductIds] = useState<number[]>([]);

  const handleSelectionChanged = useCallback(
    (event: SelectionChangedEvent<FlatProduct>) => {
      let selectedIds = [] as number[];
      event.api.forEachNode((n) => {
        if (n.isSelected() && n.data) {
          selectedIds.push(n.data.id);
        }
      });
      selectedIds = uniq(selectedIds).filter((row) => {
        return row >= 0;
      });
      setCheckedProductIds(selectedIds);
      onSelectedProductIdsChange(selectedIds);
      event.api.refreshCells({
        force: true,
        columns: ["isChecked"],
      });
    },
    [onSelectedProductIdsChange]
  );

  useEffect(() => {
    // globalSearchValue 가 변경될 때마다 강제로 리렌더링
    gridRef.current?.api?.refreshCells({
      force: true, // cell 렌더러 다시 호출
    });
  }, [query]);

  const isFiltered = isSheetTabFilterActive(query);

  const scrollToTop = () => {
    requestAnimationFrame(() => {
      gridRef.current?.api?.ensureIndexVisible(0, "top");
    });
  };

  useEffect(() => {
    scrollToTop();
  }, [query]);

  const filteredProductCount = inflatedTabs?.tabs[0]?.products?.filter(
    (product) => {
      return product.isFiltered;
    }
  )?.length;

  const totalProductCount = inflatedTabs?.tabs[0]?.products?.length || 0;

  const searchResultRowDataFiltered = useMemo(() => {
    if (rowData) {
      return [
        {
          id: -1,
          rowId: "-1_0",
          imageUrls: [],
          brandSKUEtc: "",
          genderCategorySubcategory: "",
          colorCodeColorNameMaterial: "",
          searchLinkIcons: "",
          priceAmount: -1,
          priceAdjustedByPartial: null,
          sizes: [],
          isFiltered: false,
          priceCurrency: "",
          selectable: filteredProductCount! > 0,
          applyIva: true,
        },
        {
          id: -1,
          rowId: "-1_1",
          imageUrls: [],
          brandSKUEtc: `검색/필터한 상품&nbsp;<p style="color:#1272EF">${filteredProductCount}</p>`,
          genderCategorySubcategory: "",
          colorCodeColorNameMaterial: "",
          searchLinkIcons: "",
          priceAmount: -1,
          priceAdjustedByPartial: null,
          sizes: [],
          isFiltered: false,
          priceCurrency: "",
          applyIva: true,
        },
        {
          id: -1,
          rowId: "-1_2",
          imageUrls: [],
          brandSKUEtc: "",
          genderCategorySubcategory: "",
          colorCodeColorNameMaterial: "",
          searchLinkIcons: "",
          priceAmount: -1,
          priceAdjustedByPartial: null,
          sizes: [],
          isFiltered: false,
          priceCurrency: "",
          applyIva: true,
        },
      ] as FlatProduct[];
    }
  }, [filteredProductCount, rowData]);

  const searchResultRowDataNotFiltered = useMemo(() => {
    if (rowData && isFiltered && filteredProductCount! < totalProductCount) {
      return [
        {
          id: -2,
          rowId: "-2_0",
          imageUrls: [],
          brandSKUEtc: "",
          genderCategorySubcategory: "",
          colorCodeColorNameMaterial: "",
          searchLinkIcons: "",
          priceAmount: 0,
          priceAdjustedByPartial: null,
          sizes: [],
          isFiltered: false,
          priceCurrency: "",
          applyIva: true,
        },
        {
          id: -2,
          rowId: "-2_1",
          imageUrls: [],
          brandSKUEtc: "그 외 상품",
          genderCategorySubcategory: "",
          colorCodeColorNameMaterial: "",
          searchLinkIcons: "",
          priceAmount: 0,
          priceAdjustedByPartial: null,
          sizes: [],
          isFiltered: false,
          priceCurrency: "",
          applyIva: true,
        },
        {
          id: -2,
          rowId: "-2_2",
          imageUrls: [],
          brandSKUEtc: "",
          genderCategorySubcategory: "",
          colorCodeColorNameMaterial: "",
          searchLinkIcons: "",
          priceAmount: 0,
          priceAdjustedByPartial: null,
          sizes: [],
          isFiltered: false,
          priceCurrency: "",
          applyIva: true,
        },
      ] as FlatProduct[];
    }
  }, [filteredProductCount, isFiltered, rowData, totalProductCount]);

  const noResultRow = useMemo(() => {
    return [
      {
        id: -3,
        rowId: "-3_0",
        imageUrls: [],
        brandSKUEtc: `<p class="custom-row-style-no-result-p">
<img src="${
          window.location.origin + "/warning-triangle.png"
        }" alt="네이버 검색" style="margin-right:4px;width:24px;height:24px"/>
결과가 없습니다.
</p>`,
        genderCategorySubcategory: "",
        colorCodeColorNameMaterial: "",
        searchLinkIcons: "",
        priceAmount: 0,
        priceAdjustedByPartial: null,
        sizes: [],
        isFiltered: false,
        priceCurrency: "",
        applyIva: true,
      },
      {
        id: -3,
        rowId: "-3_1",
        imageUrls: [],
        brandSKUEtc: "",
        genderCategorySubcategory: "",
        colorCodeColorNameMaterial: "",
        searchLinkIcons: "",
        priceAmount: 0,
        priceAdjustedByPartial: null,
        sizes: [],
        isFiltered: false,
        priceCurrency: "",
        applyIva: true,
      },
      {
        id: -3,
        rowId: "-3_2",
        imageUrls: [],
        brandSKUEtc: "",
        genderCategorySubcategory: "",
        colorCodeColorNameMaterial: "",
        searchLinkIcons: "",
        priceAmount: 0,
        priceAdjustedByPartial: null,
        sizes: [],
        isFiltered: false,
        priceCurrency: "",
        applyIva: true,
      },
    ] as FlatProduct[];
  }, []);

  const onRowSelected = (e: RowSelectedEvent<FlatProduct, any>) => {
    // if (e.rowIndex % 3 !== 2) {
    //   return;
    // }

    if (!isFiltered) {
      return;
    }
    const { api, node } = e;
    if (!node?.data) return;
    if (!e.data) return;
    // console.log('onRowSelected', e);

    // node.data.selected = node.isSelected();

    if (e.data.id > 0) {
      // 전체 선택을 풀어주고 묶어주는 로직 필요

      let filteredRowCount = 0;
      let unfilteredRowCount = 0;
      let checkedFilteredRowCount = 0;
      let checkedUnfilteredRowCount = 0;
      api.forEachNode((n) => {
        if (n.data && n.data.id > 0) {
          if (!n.data.rowId.includes("unfiltered")) {
            filteredRowCount += 1;
            if (n.isSelected()) {
              checkedFilteredRowCount += 1;
            }
          } else {
            unfilteredRowCount += 1;
            if (n.isSelected()) {
              checkedUnfilteredRowCount += 1;
            }
          }
        }
      });

      if (
        filteredRowCount > 0 &&
        filteredRowCount === checkedFilteredRowCount
      ) {
        api.forEachNode((n) => {
          if (n.data && n.data.id == -1) {
            // @ts-ignore
            n.selected = true;
            // n.indeterminate = false;
            n.data.indeterminate = false;
          }
        });
      } else if (filteredRowCount > 0 && checkedFilteredRowCount > 0) {
        api.forEachNode((n) => {
          if (n.data && n.data.id == -1) {
            // @ts-ignore
            n.selected = false;
            // n.indeterminate = true;
            n.data.indeterminate = true;
          }
        });
      } else {
        api.forEachNode((n) => {
          if (n.data && n.data.id == -1) {
            // @ts-ignore
            n.selected = false;
            // n.indeterminate = false;
            n.data.indeterminate = false;
          }
        });
      }
      if (
        unfilteredRowCount > 0 &&
        unfilteredRowCount === checkedUnfilteredRowCount
      ) {
        api.forEachNode((n) => {
          if (n.data && n.data.id == -2) {
            // @ts-ignore
            n.selected = true;
            // n.indeterminate = false;
            n.data.indeterminate = false;
          }
        });
      } else if (unfilteredRowCount > 0 && checkedUnfilteredRowCount > 0) {
        api.forEachNode((n) => {
          if (n.data && n.data.id == -2) {
            // @ts-ignore
            n.selected = false;
            // n.indeterminate = true;
            n.data.indeterminate = true;
          }
        });
      } else {
        api.forEachNode((n) => {
          if (n.data && n.data.id == -2) {
            // @ts-ignore
            n.selected = false;
            // n.indeterminate = false;
            n.data.indeterminate = false;
          }
        });
      }
      return;
    }

    const shouldSelect = node.isSelected();
    const startIndex = node.rowIndex;

    if (startIndex === null) return;
    if (shouldSelect === undefined) return;

    node.data.indeterminate = false;

    let i = startIndex + 1;
    while (true) {
      const next = api.getDisplayedRowAtIndex(i);
      if (!next) break;

      // 다음 헤더면 중단
      if (next.data && next.data?.id < 0) break;

      if (next.data && next.data.id > 0) {
        // @ts-ignore
        next.selected = shouldSelect;
      }

      i += 1;
    }
  };

  if (isLoading) {
    return <CenteredSpinner />;
  }
  const rowClassMap: Record<number, string> = {
    [-1]: "custom-row-style-filtered",
    [-2]: "custom-row-style-not-filtered",
    [-3]: "custom-row-style-no-result",
  };

  let rowDataFinal: FlatProduct[] | undefined = [];
  if (isFiltered) {
    if (filteredProductCount === 0) {
      rowDataFinal = [...noResultRow];
    } else {
      rowDataFinal = rowData;
    }
    if (searchResultRowDataFiltered) {
      rowDataFinal = [...searchResultRowDataFiltered, ...(rowDataFinal || [])];
    }
    if (searchResultRowDataNotFiltered && rowDataNotFiltered.length > 0) {
      rowDataFinal = [
        ...(rowDataFinal || []),
        ...searchResultRowDataNotFiltered,
        ...rowDataNotFiltered,
      ];
    }
  } else {
    rowDataFinal = rowData;
  }

  return (
    <Box
      className={"ag-theme-alpine"}
      paddingBottom={"48px"}
      height={"100%"}
      width={"100%"}
      fontFamily={"Inter"}
      position={"relative"}
      flex={1}
      sx={{
        "& > div:nth-of-type(2)": {
          paddingLeft: isOrderRulesPanelOpen ? "334px" : "0px",
        },
      }}
    >
      {isOrderRulesPanelOpen && (
        <Box
          position={"absolute"}
          left={0}
          zIndex={9}
          height={"calc(100% - 32px)"}
        >
          <SheetInfoPanel
            onClose={handleOrderRulesToggle!}
            showMoqNotice
            lineSheetSet={getDenseOrderSheetSetData?.lineSheetSet}
            orderSheetSet={
              getDenseOrderSheetSetData as unknown as OrderSheetSet
            }
          />
        </Box>
      )}
      <AgGridReact<FlatProduct>
        ref={handleSetGridRef}
        suppressRowTransform={true}
        readOnlyEdit={true}
        // suppressClickEdit={true}
        navigateToNextCell={() => null}
        // singleClickEdit={true}
        getRowId={getRowId}
        headerHeight={24}
        columnDefs={displayedColDefs}
        rowData={rowDataFinal}
        //왜 25를 해야 24가 되는거야?;;
        getRowClass={(params) => {
          const id = params.data?.id;
          if (id === undefined) {
            return "";
          }
          return rowClassMap[id] || "";
        }}
        onRowSelected={onRowSelected}
        rowHeight={25}
        rowModelType={"clientSide"}
        onCellKeyDown={onCellKeyDown}
        onCellEditRequest={onCellEditRequest}
        onCellEditingStarted={onCellEditingStarted}
        onCellEditingStopped={onCellEditingStopped}
        defaultColDef={defaultColDef}
        onGridReady={handleGridReady}
        rowSelection={"multiple"}
        // isRowSelectable={isRowSelectable}
        suppressRowClickSelection={true}
        onSelectionChanged={handleSelectionChanged}
        enableCellTextSelection={true}
        components={components}
        suppressRowHoverHighlight={true}
        columnHoverHighlight={false}
        suppressMovableColumns={true}
        noRowsOverlayComponent={NoResults}
        noRowsOverlayComponentParams={{
          isBrandFiltered:
            query.brandName__eq && query.brandName__eq.length > 0,
          handleResetFilter: () => {
            setOrderSheetSearchKeyword("");
            setQuery(
              (prev) => {
                return {
                  ...prev,
                  ...EMPTY_SHEET_TAB_FILTER_QUERY,
                };
              },
              {
                replace: true,
              }
            );
          },
        }}
        animateRows={false}
        rowBuffer={10}
        suppressRowVirtualisation={false}
        suppressColumnVirtualisation={false}
        suppressDragLeaveHidesColumns={true}
      />
      <OrderSheetTabBottomBar
        sheetKey={sheetKey}
        exchangeCurrencyValue={exchangeCurrencyValue}
        selectedCurrency={selectedCurrency}
        checkedProductIds={checkedProductIds}
        isFiltered={isFiltered}
        orderStatus={"ORDERED"}
        commission={lineSheetSetState?.lineSheetSet?.commission}
        commissionType={lineSheetSetState?.lineSheetSet?.commissionType}
      />
      {marketSizeProductId != null && (
        <OrderSheetMarketSizeModal
          product={marketSizeProduct}
          marketPrice={
            marketPriceByProductId?.get(marketSizeProductId) ?? null
          }
          pricing={marketPricing}
          onClose={() => setMarketSizeProductId(null)}
        />
      )}
      {marketExternalModal}
    </Box>
  );
}

// BuyerOrderSheet.whyDidYouRender = true;


